/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { JobDetailsSchema } from '@/commons/schemas';
import IntegrationStatusIcon, { mapJobStatusToIntegrationStatus } from '@/components/IntegrationStatusIcon';
import Sidebar from '@/components/Sidebar';
import Tooltip from '@/components/Tooltip';
import { useTranslations } from '@/hooks/useTranslations';
import { useGetJobsByProductRunIdQuery, useGetProductRunByUserIdAndFlowIdQuery, useGetUserInfoQuery } from '@/store/apis';
import { createStyles } from '@/utils/styles';
import '@ui5/webcomponents-icons/dist/in-progress-2.js';
import {
    Bar,
    Button,
    Card,
    FlexBox,
    Icon,
    IllustratedMessage,
    Table,
    TableCell,
    TableHeaderCell,
    TableHeaderRow,
    TableRow,
    Text,
    Title,
} from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';

import { Link } from 'react-router-dom';

export function parseJobDetail(detail: string) {
    try {
        return JobDetailsSchema.parse(JSON.parse(detail));
    } catch (error) {
        console.error('Failed to parse job details', error);
        return null;
    }
}

export default function IntegrationMonitorPage({ skuId, flowId }: { skuId: number; flowId: string }) {
    const { t } = useTranslations();
    const { data: userInfo } = useGetUserInfoQuery();
    const { data: productRun } = useGetProductRunByUserIdAndFlowIdQuery(
        {
            userId: userInfo?.scim_id ?? '',
            flowId,
        },
        { skip: skuId !== 1 || !userInfo || !flowId }
    );
    const { data: jobs, refetch } = useGetJobsByProductRunIdQuery(productRun?.id ?? '', { skip: !productRun });

    if (skuId !== 1) {
        console.error('Invalid SKU ID', skuId);
        return (
            <IllustratedMessage
                name="PageNotFound"
                titleText={t('INTEGRATION_MONITOR_PAGE_INVALID_SKU_TITLE')}
                subtitleText={t('INTEGRATION_MONITOR_PAGE_INVALID_SKU_SUBTITLE')}
            >
                <Link to="/">
                    <Button>{t('MONITOR_LAYOUT_BACK_TO_HOME_BUTTON_TEXT')}</Button>
                </Link>
            </IllustratedMessage>
        );
    }

    return (
        <div style={styles.wrapper}>
            <Bar design="Subheader" startContent={<Title size="H3">{t('INTEGRATION_MONITOR_PAGE_TITLE')}</Title>} />
            <FlexBox gap={16} style={styles.contentWrapper} wrap="Wrap" alignItems="Start">
                <Sidebar
                    headerText={t('MONITOR_PROGRESS')}
                    items={[
                        {
                            label: t('INTEGRATION_MONITOR_PAGE_42K_TITLE'),
                        },
                    ]}
                    maxWidth={500}
                />

                <Card style={{ maxWidth: 864 }}>
                    <div style={{ padding: 20 }}>
                        <FlexBox gap={14} direction="Column">
                            <Title>{t('INTEGRATION_MONITOR_PAGE_42K_TITLE')}</Title>

                            <Table
                                style={{ width: '100%' }}
                                headerRow={
                                    <TableHeaderRow
                                        sticky
                                        style={{
                                            borderLeft: 'none',
                                            borderRight: 'none',
                                            borderTop: `var(--sapList_BorderWidth) solid var(--sapList_HeaderBorderColor)`,
                                        }}
                                    >
                                        <TableHeaderCell>
                                            <span>S/4Hana System</span>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <span>Ariba Network</span>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <span>{t('INTEGRATION_MONITOR_PAGE_STATUS_COLUMN_HEADER_TEXT')}</span>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <span>{t('INTEGRATION_MONITOR_PAGE_LAST_MODIFIED_COLUMN_HEADER_TEXT')}</span>
                                        </TableHeaderCell>
                                    </TableHeaderRow>
                                }
                            >
                                {jobs?.map(job => {
                                    const detail = parseJobDetail(job.detail);

                                    return (
                                        <TableRow
                                            key={job.id}
                                            style={{
                                                borderLeft: 'none',
                                                borderRight: 'none',
                                            }}
                                        >
                                            <TableCell>
                                                <span>{detail?.buyerAccountSBN.id || '-'}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span>{detail?.s4HanaPC.id || '-'}</span>
                                            </TableCell>
                                            <TableCell>
                                                <IntegrationStatusIcon status={mapJobStatusToIntegrationStatus(job.status)} />
                                            </TableCell>
                                            <TableCell>
                                                <FlexBox justifyContent="SpaceBetween" style={{ width: '100%' }}>
                                                    <span>{job.updatedAt}</span>
                                                    <Tooltip
                                                        content={
                                                            <FlexBox direction="Column">
                                                                <FlexBox gap={4}>
                                                                    <Title size="H6">
                                                                        {t('INTEGRATION_MONITOR_PAGE_JOB_TABLE_JOB_ID_COLUMN_HEADER')}:
                                                                    </Title>
                                                                    <Text>{job.id}</Text>
                                                                </FlexBox>
                                                                <FlexBox gap={4}>
                                                                    <Title size="H6">
                                                                        {t('INTEGRATION_MONITOR_PAGE_JOB_TABLE_CREATE_AT_COLUMN_HEADER')}:
                                                                    </Title>
                                                                    <Text>{job.createdAt}</Text>
                                                                </FlexBox>
                                                            </FlexBox>
                                                        }
                                                    >
                                                        <Icon
                                                            name="overflow"
                                                            style={{
                                                                color: ThemingParameters.sapNeutralElementColor,
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </FlexBox>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        </FlexBox>
                    </div>
                </Card>
            </FlexBox>

            <div style={styles.footerWrapper}>
                <Bar
                    design="FloatingFooter"
                    endContent={
                        <FlexBox style={{ gap: 8 }}>
                            <Button type="Button" design="Default" icon="refresh" onClick={refetch}>
                                {t('REFRESH_BUTTON')}
                            </Button>
                            <Link to="/">
                                <Button type="Button" design="Emphasized">
                                    {t('HOME_BUTTON')}
                                </Button>
                            </Link>
                        </FlexBox>
                    }
                />
            </div>
        </div>
    );
}

const styles = createStyles({
    wrapper: {
        height: '100vh',
        overflowY: 'auto',
    },

    contentWrapper: {
        padding: '16px 48px',
        gap: 16,
    },
    footerWrapper: {
        padding: 8,
        left: 0,
        bottom: 0,
        width: '100%',
        position: 'fixed',
    },
});
